<template lang="html">
  <v-flex pa-0>
    <v-slide-x-transition
      leave-absolute
      hide-on-leave
    >
      <v-container
        v-if="!$route.query.fieldId"
        grid-list-lg
      >
        <v-layout wrap>
          <v-flex
            xs12
            lg10
            xl8
            mx-auto
          >
            <v-layout wrap>
              <v-flex xs12>
                <v-card>
                  <v-card-title class="hedline">
                    Fields
                    <v-spacer />

                    <v-btn
                      text
                      color="primary"
                      @click="newField = true"
                    >
                      <v-icon left>
                        add
                      </v-icon>add
                    </v-btn>
                  </v-card-title>
                  <v-list class="pa-0">
                    <template v-for="field in sortedFields">
                      <v-divider :key="`divider_${field.id}`" />
                      <v-list-item
                        :key="field.id"
                        @click="openField(field.name)"
                      >
                        <v-list-item-icon>
                          <v-icon class="mr-3">
                            {{ (_inputTypes[field.inputType] || {}).icon }}
                          </v-icon>
                          <v-tooltip
                            right
                            :disabled="!field.condition"
                          >
                            <template #activator="{ on }">
                              <v-icon
                                v-if="field.condition"
                                color="warning"
                                v-on="on"
                              >
                                call_split
                              </v-icon>
                              <v-icon
                                v-else
                                v-on="on"
                              >
                                arrow_upward
                              </v-icon>
                            </template>
                            <SiConditionChips :condition="field.condition" />
                          </v-tooltip>
                          <v-icon
                            class="mx-3"
                            :color="field.required ? 'info' : 'grey lighten-3'"
                          >
                            priority_high
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title>{{ field.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{ field.label }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-icon>
                          <v-icon
                            v-if="field.rules"
                            class="ml-3"
                            color="warning"
                          >
                            details
                          </v-icon>
                          <v-icon
                            v-if="field.inputType === 'SiMultiItemInput'"
                            class="ml-3"
                            color="blue"
                          >
                            playlist_add
                          </v-icon>
                          <v-icon
                            v-if="isInMulti(field.name)"
                            class="ml-3"
                            color="grey"
                          >
                            playlist_play
                          </v-icon>
                          <v-icon
                            v-if="field.reportHide"
                            class="ml-3"
                            color="error"
                          >
                            visibility_off
                          </v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-card>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-container>
    </v-slide-x-transition>

    <v-slide-x-reverse-transition
      leave-absolute
      hide-on-leave
    >
      <v-container
        v-if="$route.query.fieldId"
        grid-list-lg
      >
        <v-layout wrap>
          <v-flex
            xs12
            lg10
            xl8
            mx-auto
          >
            <v-layout wrap>
              <v-flex
                xs12
                mb-4
              >
                <v-layout
                  ma-0
                  align-center
                >
                  <v-btn
                    small
                    depressed
                    fab
                    color="primary"
                    class="mr-4"
                    @click="closeField"
                  >
                    <v-icon>arrow_back</v-icon>
                  </v-btn>
                  <span class="headline grey--text">{{ field.name }}</span>
                </v-layout>
              </v-flex>
              <v-flex
                v-if="!['SiInputCalculated', 'SiMultiItemInput'].includes(field.inputType)"
                xs12
              >
                <v-card>
                  <v-card-title class="headline">
                    Preview
                  </v-card-title>
                  <v-card-text>
                    <component
                      :is="field.inputType"
                      v-bind="field"
                      :rules="[]"
                      filled
                    />
                  </v-card-text>
                </v-card>
              </v-flex>
              <v-flex xs12>
                <v-card>
                  <v-card-title class="headline">
                    Configuration
                  </v-card-title>
                  <v-card-text>
                    <FieldsEditor
                      v-model="field"
                      :fields="fields"
                      :calculator-id="calculatorId"
                      @close="closeField"
                    />
                  </v-card-text>
                </v-card>
              </v-flex>
              <v-flex xs12>
                <v-card>
                  <v-card-title class="headline">
                    Usage
                  </v-card-title>
                  <v-card-text>
                    <v-treeview
                      item-key="id"
                      open-all
                      :items="fieldUsage(field.name)"
                    >
                      <template #prepend="{ item }">
                        <v-icon>
                          {{ item.type === 'form' ? 'description' : 'short_text' }}
                        </v-icon>
                      </template>
                      <template #label="{ item }">
                        <router-link
                          class="si-form-section-link"
                          :to="item.link"
                        >
                          {{ item.name }}
                        </router-link>
                      </template>
                    </v-treeview>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-container>
    </v-slide-x-reverse-transition>

    <v-dialog
      v-model="newField"
      max-width="600"
    >
      <v-card>
        <v-card-text>
          <SiVariableEditor
            ref="new_field"
            variable-term="field name"
            active
            justify-end
            :existing-variables="fields"
            :min-length="2"
            :allow-explicit="['_error']"
            @variable="addField"
            @cancel="newField = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex'
import { Container, Draggable } from 'vue-smooth-dnd'
import { forEach, includes } from 'lodash'
import { rtdb } from '@/plugins/firebase'
import firebase from '@/mixins/firebase'
import helpers from '@/mixins/helpers'
import drag from '@/mixins/drag'
import conditions from '@/mixins/conditions'
import FieldsEditor from './components/FieldsEditor.vue'
export default {
  components: { 
    Container, 
    Draggable,
    FieldsEditor
  },
  mixins: [ helpers, drag, firebase, conditions ],
  props: {
    fields: Object,
    calculatorId: String,
    mapping: Object,
  },
  data () {
    return {
      panel: -1,
      newField: false,
      newFieldName: '',
      newFieldSaving: false,
      showConditions: false,
      sortedFields: this._objectToArrayAplha(this.fields, 'name'),

      // dialog: 0,
      activeField: null
    }
  },
  computed: {
    ...mapGetters(['_inputTypes', '_inputTypesItemsArr']),
    window () {
      return this.$route.query.fieldId ? 1 : 0
    },
    field () {
      return this.fields[this.$route.query.fieldId] || {}
    },
    inMulti () {
      var inMulti = []
      forEach(this.fields, f => {
        inMulti = inMulti.concat(f.fields) || []
      })
      return inMulti.filter(x => {
        return x
      })
    }
  },
  watch: {
    fields () {
      this.sortedFields = this._objectToArrayAplha(this.fields, 'name')
    },
    '$route.query.scroll' (v, ov) {
      if (!v) {
        setTimeout(() => {
          this.$vuetify.goTo(Number(ov + 60), { duration: 0 })
        }, 600)
      }
    },
    newField () {
      requestAnimationFrame(() => {
        this.$refs.new_field.focus()
      })
    }
  },
  methods: {
    openField (fieldId) {
      this.$router.push({ path: this.$route.path, query: { fieldId: fieldId, scroll: window.scrollY } })
      // this.$vuetify.goTo(184, { duration: 0 })
    },
    closeField () {
      this.$router.replace({ path: this.$route.path, query: { } })
    },
    isInMulti (fieldName) {
      return includes(this.inMulti, fieldName)
    },
    fieldAttrs (type) {
      return (this._inputTypes[type] || {}).attrs || {}
    },
    fieldPath (fieldName) {
      return `calculators/${this.calculatorId}/structure/fields/${fieldName}`
    },
    fieldValidator (field) {
      let itemsValid = []
      let labelValid = false
      let fieldsValid = false
      if (((this.fieldAttrs(field.inputType) || {}).attrs || {}).items) {
        field.items.forEach(item => {
          itemsValid.push(!!item.text && item.valu !== 'undefined')
        })
      }
      if (this.fieldAttrs(field.inputType).label) {
        labelValid = !!field.label
      } else {
        labelValid = true
      }

      if (this.fieldAttrs(field.inputType).fields) {
        fieldsValid = (field.fields || []).length > 0
      } else {
        fieldsValid = true
      }
      return !!field.inputType && labelValid && fieldsValid && itemsValid.every(Boolean)
    },
    addField (fieldName) {
      var field = { name: fieldName, required: true }
      this.newFieldSaving = true

      this.sortedFields = this._objectToArrayAplha(Object.assign({}, {[fieldName]: field}, this.fields), 'name')

      rtdb.ref(this.fieldPath(fieldName)).set(field, () => {
        this.openField(fieldName)
      })
    },
    fieldUsage (fieldName) {
      let formTree = []
      let { forms, initialSection } = this.mapping
      let { sub } = this.$route.params

      const sectionLink = (formId, sectionId) => `/admin/calculators/${sub}/forms?formId=${formId}${sectionId ? `&sectionId=${sectionId}`: ''}&fieldName=${fieldName}`

      const iteratSections = (sections, formName, formId) => {
        let formBranch = { 
          type: 'form', 
          id: formId, 
          name: formName || formId, 
          children: [], 
          formId, 
          link: sectionLink(formId)
          }
        forEach(sections, (section, sectionId) => {
          if ((section.fields || []).includes(fieldName)) {
            formBranch.children.push({ 
              type: 'section',
              id: `${formId}_${sectionId}`,
              name: `${section.title || '[no title]'} - ${section.id}`, 
              formId, 
              sectionId, 
              link: sectionLink(formId, sectionId)
            })
          }
        })
        if (formBranch.children.length > 0) formTree.push(formBranch)
      }

      forEach(forms, (form, formId) => iteratSections(form.sections, form.name, formId))

      iteratSections(initialSection, 'Initial sections', 'initialSections')

return formTree
    },
  }
}
</script>

<style lang="css" scoped>

.si-form-section-link {
  text-decoration: none;
}

.si-expansion-panel-inset.v-expansion-panels--inset .v-expansion-panel__container:not(.v-expansion-panel__container--active) {
    max-width: 100%;
}
.si-expansion-panel-inset.v-expansion-panels--inset .v-expansion-panel__container--active {
    max-width: 95%;
}
</style>
